import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { FaCompress, FaExpand } from "react-icons/fa";
import ReadingProgressBar from "./ReadingProgressBar";
import { ARTICLES } from "../constants/Articles";

const createSlug = (text) => text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&amp;|&/g, " and ")
    .replace(/[`*_~]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const getHeadings = (markdown) => {
    const occurrences = new Map();

    return markdown.split("\n").flatMap((line, index) => {
        const match = line.match(/^(#{1,3})\s+(.+?)\s*#*\s*$/);
        if (!match) return [];

        const title = match[2]
            .replace(/!?\[([^\]]+)\]\([^)]*\)/g, "$1")
            .replace(/<[^>]*>/g, "")
            .replace(/[`*_~]/g, "")
            .trim();
        const slug = createSlug(title) || `section-${index + 1}`;
        const occurrence = occurrences.get(slug) || 0;
        occurrences.set(slug, occurrence + 1);

        return [{
            id: occurrence ? `${slug}-${occurrence}` : slug,
            level: match[1].length,
            line: index + 1,
            title,
        }];
    });
};

const Article = () => {

    const { postId } = useParams();
    const [content, setContent] = useState("");
    const [isFocusMode, setIsFocusMode] = useState(false);
    const [activeHeading, setActiveHeading] = useState("");
    const articleIndex = ARTICLES.findIndex((article) => article.postId === postId);
    const previousArticle = articleIndex > 0 ? ARTICLES[articleIndex - 1] : null;
    const nextArticle = articleIndex >= 0 && articleIndex < ARTICLES.length - 1
        ? ARTICLES[articleIndex + 1]
        : null;
    const headings = getHeadings(content);
    const headingsByLine = new Map(headings.map((heading) => [heading.line, heading]));

    useEffect(() => {
        fetch(`/posts/${postId}.md`)
            .then((res) => {
                return res.text()
            })
            .then((text) => setContent(text))
            .catch((_) => {});
    }, [postId]);

    useEffect(() => {
        document.body.classList.toggle("article-focus-mode", isFocusMode);
        return () => document.body.classList.remove("article-focus-mode");
    }, [isFocusMode]);

    useEffect(() => {
        const headingElements = document.querySelectorAll("[data-article-heading]");
        if (!headingElements.length) return undefined;

        const observer = new IntersectionObserver((entries) => {
            const visibleEntries = entries
                .filter((entry) => entry.isIntersecting)
                .sort((first, second) => first.boundingClientRect.top - second.boundingClientRect.top);

            if (visibleEntries.length) {
                setActiveHeading(visibleEntries[0].target.id);
            }
        }, { rootMargin: "-15% 0px -70% 0px" });

        headingElements.forEach((heading) => observer.observe(heading));
        return () => observer.disconnect();
    }, [content]);

    const renderHeading = (Tag, className) => ({ node, children, ...props }) => {
        const heading = headingsByLine.get(node?.position?.start?.line);
        return (
            <Tag
                id={heading?.id}
                data-article-heading={heading ? "true" : undefined}
                className={`scroll-mt-28 ${className}`}
                {...props}
            >
                {children}
            </Tag>
        );
    };

    return (
        <div>
            <ReadingProgressBar />
            <article className={`min-h-screen py-10 sm:py-14 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-950 transition-colors duration-300 ${isFocusMode ? "article-focus-reading" : ""}`}>
                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-end mb-6">
                        <button
                            type="button"
                            onClick={() => setIsFocusMode((current) => !current)}
                            aria-pressed={isFocusMode}
                            className="inline-flex items-center gap-2 rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:border-blue-500 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {isFocusMode ? <FaCompress aria-hidden="true" /> : <FaExpand aria-hidden="true" />}
                            {isFocusMode ? "Exit focus mode" : "Focus mode"}
                        </button>
                    </div>

                    <div className={`grid grid-cols-1 gap-10 ${isFocusMode ? "max-w-4xl mx-auto" : "lg:grid-cols-[minmax(0,1fr)_16rem]"}`}>
                        <div>
                            <div className="prose prose-sm sm:prose-base lg:prose-lg prose-slate dark:prose-invert max-w-none">
                        <ReactMarkdown
                            components={{
                                h1: renderHeading("h1", "text-4xl sm:text-5xl md:text-6xl font-bold mb-6 text-slate-900 dark:text-white"),
                                h2: renderHeading("h2", "text-3xl sm:text-4xl font-bold mb-4 mt-8 text-slate-900 dark:text-white"),
                                h3: renderHeading("h3", "text-2xl sm:text-3xl font-bold mb-3 mt-6 text-slate-900 dark:text-white"),
                                p: ({ node, ...props }) => <p className="text-base sm:text-lg text-slate-700 dark:text-slate-300 mb-4 leading-relaxed" {...props} />,
                                ul: ({ node, ...props }) => <ul className="list-disc list-inside text-base sm:text-lg text-slate-700 dark:text-slate-300 mb-6 space-y-2" {...props} />,
                                ol: ({ node, ...props }) => <ol className="list-decimal list-inside text-base sm:text-lg text-slate-700 dark:text-slate-300 mb-6 space-y-2" {...props} />,
                                li: ({ node, ...props }) => <li className="text-base sm:text-lg text-slate-700 dark:text-slate-300" {...props} />,
                                blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-blue-600 dark:border-blue-400 pl-4 py-2 my-6 italic text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-r-lg" {...props} />,
                                code: ({ node, inline, ...props }) => 
                                    inline ? (
                                        <code className="bg-slate-100 dark:bg-slate-800 text-red-600 dark:text-red-400 px-2 py-1 rounded font-mono text-sm" {...props} />
                                    ) : (
                                        <code className="block bg-slate-900 dark:bg-slate-950 text-slate-100 dark:text-slate-100 p-4 rounded-lg overflow-x-auto font-mono text-sm mb-6 my-4" {...props} />
                                    ),
                                a: ({ node, children, ...props }) => <a className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline transition-colors duration-200" {...props}>{children}</a>,
                                img: ({ node, alt, ...props }) => <img alt={alt || ""} className="max-w-full h-auto rounded-lg my-6 shadow-md dark:shadow-lg" {...props} />,
                                hr: ({ node, ...props }) => <hr className="my-8 border-slate-300 dark:border-slate-700" {...props} />,
                                table: ({ node, ...props }) => <div className="overflow-x-auto my-6"><table className="border-collapse border border-slate-300 dark:border-slate-700 w-full" {...props} /></div>,
                                th: ({ node, ...props }) => <th className="border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-4 py-2 text-left font-semibold text-slate-900 dark:text-white" {...props} />,
                                td: ({ node, ...props }) => <td className="border border-slate-300 dark:border-slate-700 px-4 py-2 text-slate-700 dark:text-slate-300" {...props} />,
                            }}
                        >
                            {content}
                        </ReactMarkdown>
                            </div>

                            <nav aria-label="Article navigation" className="mt-14 border-t border-slate-200 dark:border-slate-700 pt-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {previousArticle ? (
                                        <Link to={`/blog/${previousArticle.postId}`} className="group text-left">
                                            <span className="block text-sm text-slate-500 dark:text-slate-400">Previous article</span>
                                            <span className="mt-1 block font-semibold text-slate-800 dark:text-slate-100 group-hover:text-blue-700 dark:group-hover:text-blue-300">{previousArticle.title}</span>
                                        </Link>
                                    ) : <span />}
                                    {nextArticle && (
                                        <Link to={`/blog/${nextArticle.postId}`} className="group text-left sm:text-right">
                                            <span className="block text-sm text-slate-500 dark:text-slate-400">Next article</span>
                                            <span className="mt-1 block font-semibold text-slate-800 dark:text-slate-100 group-hover:text-blue-700 dark:group-hover:text-blue-300">{nextArticle.title}</span>
                                        </Link>
                                    )}
                                </div>
                            </nav>
                        </div>

                        {!isFocusMode && headings.length > 0 && (
                            <>
                            <details className="lg:hidden order-first rounded-md border border-slate-200 dark:border-slate-700 p-4">
                                <summary className="cursor-pointer font-semibold text-slate-800 dark:text-slate-100">On this page</summary>
                                <ul className="mt-3 space-y-1">
                                    {headings.map((heading) => (
                                        <li key={`mobile-${heading.id}-${heading.line}`}>
                                            <a
                                                href={`#${heading.id}`}
                                                aria-current={activeHeading === heading.id ? "location" : undefined}
                                                className={`block py-1 text-sm ${heading.level === 1 ? "pl-0" : heading.level === 2 ? "pl-3" : "pl-6"} ${activeHeading === heading.id ? "font-semibold text-blue-700 dark:text-blue-300" : "text-slate-600 dark:text-slate-400"}`}
                                            >
                                                {heading.title}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </details>
                            <aside className="hidden lg:block">
                                <nav aria-label="On this page" className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto border-l border-slate-200 dark:border-slate-700 pl-4">
                                    <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">On this page</h2>
                                    <ul className="space-y-1">
                                        {headings.map((heading) => (
                                            <li key={`${heading.id}-${heading.line}`}>
                                                <a
                                                    href={`#${heading.id}`}
                                                    aria-current={activeHeading === heading.id ? "location" : undefined}
                                                    className={`block border-l-2 py-1 text-sm transition-colors ${heading.level === 1 ? "pl-3" : heading.level === 2 ? "pl-5" : "pl-7"} ${activeHeading === heading.id ? "-ml-[18px] border-blue-600 font-semibold text-blue-700 dark:border-blue-400 dark:text-blue-300" : "-ml-[18px] border-transparent text-slate-600 hover:text-blue-700 dark:text-slate-400 dark:hover:text-blue-300"}`}
                                                >
                                                    {heading.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            </aside>
                            </>
                        )}
                    </div>
                </div>
            </article>
        </div>

    );
};

export default Article;